import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const items = [
  { num: "01", label: "Jewellery — Any Karat", body: "Scrap Gold — Chains, bracelets, rings, broken pieces." },
];

export default function BuyGold() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["12%", "-12%"]);

  return (
    <section
      ref={ref}
      id="buy-gold"
      data-testid="buy-gold-section"
      className="relative bg-[#E5D5C1] text-[#1C1C1A] px-6 md:px-10 py-28 md:py-40 overflow-hidden"
    >
      {/* Overline row */}
      <div className="flex items-center justify-between border-b border-[#1C1C1A]/20 pb-6 mb-16">
        <span className="text-[10px] uppercase tracking-[0.32em] font-sans">
          Ch. 06 — Cash for Gold
        </span>
        <span className="text-[10px] uppercase tracking-[0.32em] font-sans hidden md:inline">
          Paid on the spot · Certified scales
        </span>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        {/* Left — huge headline */}
        <motion.div style={{ y: y1 }} className="md:col-span-7">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-light leading-[0.86] tracking-[-0.02em] text-[16vw] md:text-[12vw] lg:text-[10vw]"
          >
            We Buy
            <br />
            <span className="italic">Gold.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 font-serif text-xl md:text-2xl leading-snug max-w-xl"
          >
            Turn old pieces into something new — or into cash. Honest weight,
            live market rates.
          </motion.p>

          <motion.a
            href="#contact"
            data-testid="buy-gold-cta"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-10 group inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] font-sans border border-[#1C1C1A] px-6 py-4 hover:bg-[#1C1C1A] hover:text-[#E5D5C1] transition-colors duration-500"
          >
            Visit Us
            <span
              aria-hidden="true"
              className="transition-transform duration-500 group-hover:translate-x-1"
            >
              →
            </span>
          </motion.a>
        </motion.div>

        {/* Right — items list */}
        <motion.ul
          style={{ y: y2 }}
          className="md:col-span-5 md:mt-6 divide-y divide-[#1C1C1A]/20 border-y border-[#1C1C1A]/20"
        >
          {items.map((it, i) => (
            <motion.li
              key={it.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group py-6 md:py-7 flex items-start gap-6"
              data-testid={`buy-gold-item-${it.num}`}
            >
              <div className="flex-1">
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">
                  {it.label}
                </h3>
                <p className="mt-2 font-sans text-sm md:text-base text-[#1C1C1A]/75 max-w-md">
                  {it.body}
                </p>
              </div>
            </motion.li>
          ))}
        </motion.ul>
      </div>

      {/* Bottom row of trust markers */}
      <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-3 border-t border-[#1C1C1A]/20 pt-8 gap-6">
        {[
          ["25+ Yrs", "Trusted goldsmith"],
          ["Best Rates", "No appointment needed"],
          ["On the Spot", "Cash · E-Transfer · Cheque"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="font-serif text-2xl md:text-3xl leading-none">{k}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.28em] font-sans text-[#1C1C1A]/70">
              {v}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
